/** Where a screen was reached from — card C-A, gate 32: *"No user menu, no search, no breadcrumbs."*
 *
 * `nav[aria-label="Breadcrumb"] > ol` (`BUILD/contracts.md` §2). Rendered **only for a screen that
 * is reached from another** — a record from the register, a review from the evidence library, an
 * import from its review, the profile and settings from the menu. A rail destination has none: it
 * *is* where the rail leads, and a trail of one is a heading said twice.
 *
 * **This is not the `crumb` subtitle**, which stays. That is a phrase in the top bar restating what
 * the screen is (*"how this workspace is set up"*); this is a path back. The two answer different
 * questions and `qa/KEEP.md` protects the first.
 *
 * **The trail is derived from the address, not remembered.** A trail built from the history of
 * clicks is wrong after a refresh and wrong in a shared link, which is exactly the case the URL
 * contract (`qa/KEEP.md` item 2) exists to make right. A record opened from a pasted link still
 * says it belongs under the emissions inventory.
 *
 * The last item is the screen itself, marked `aria-current="page"` and not a link. Parents carry
 * the method and the period across, as the rail does, so going back does not silently change which
 * Scope 2 inventory or which year is being read.
 */

import { Link } from "@tanstack/react-router";

import type { Scope2Method } from "../api/queries";
import type { Window } from "../screens/useScreen";
import styles from "./Breadcrumbs.module.css";
import { parentsOf } from "./breadcrumbTrail";
import { recordOrigin } from "./navigationTrail";
import type { RecordPlace } from "./routeTable";

export function Breadcrumbs({
  pathname,
  title,
  method,
  period,
  labelOf,
  lights,
}: {
  pathname: string;
  /** The screen's own name — the last item. */
  title: string;
  method: Scope2Method;
  period: Window | undefined;
  labelOf: (to: string) => string | undefined;
  /** A record page's own answer for where it belongs (`recordPlace`), which its address cannot say. */
  lights?: RecordPlace;
}) {
  const parents = parentsOf(pathname, labelOf, { lights, origin: recordOrigin() });
  if (!parents) return null;

  return (
    <nav aria-label="Breadcrumb" className={styles.nav} data-testid="breadcrumbs">
      <ol className={styles.list}>
        {parents.map((parent) => (
          <li key={parent.to} className={styles.item}>
            <Link
              to={parent.to}
              className={styles.link}
              // The method and the period travel with the reader, as they do on the rail. A list the
              // reader opened a record from comes back with its own filters and window, verbatim.
              search={(prev: Record<string, unknown>) =>
                parent.search ?? {
                  ...prev,
                  method,
                  from: period?.period_start,
                  to: period?.period_end,
                  // The parent's tab, or none: the child's own `tab` (a record's History) must not be
                  // carried to a list that means something else by it.
                  tab: parent.tab,
                }
              }
              data-testid="breadcrumb-parent"
            >
              {parent.label}
            </Link>
          </li>
        ))}
        <li className={styles.item} aria-current="page">
          {title}
        </li>
      </ol>
    </nav>
  );
}
